// Contracts list — read-only table with paired-contract grouping.
//
// Drives the Bunkers tab (and the Drafts / Inactive tab via different
// listParams). Server-side filtering for is_active is handled by the
// parent dashboard; this component adds the free-text search box and
// renders an inline empty state when zero contracts match.
//
// Visual grouping mirrors the Bunker Operations Trade Recaps tab. Two
// contracts that share a paired_contract FK render as a single
// "pair" in the table, joined by a vertical accent bar in a dedicated
// 32px column on the left. The pattern + CSS class names are a
// verbatim port of the recaps table in TradeOperationsLayout.js so
// the trader's mental model carries across modules.
//
// File reference: feature_planning/bunkers_contracts/04_legal_module_layout.md

import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faFileSignature,
  faFile,
  faLink,
} from '@fortawesome/free-solid-svg-icons';

import HMSBadge from '../../../components/ui/HMSBadge';
import HMSEmptyState from '../../../components/ui/HMSEmptyState';
import { expiryFlag, expiryText } from '../../../components/Legal/contractExpiry';
import HMSSkeleton from '../../../components/ui/HMSSkeleton';
import { legalContractService } from '../../../services/legalContractService';
// The table keeps its own markup — the pair-grouping accent column is built
// by hand — and takes the shared look through the `.hms-table-wrap` /
// `.hms-table` contract, so it needs the stylesheet rather than the HMSTable
// renderer. Imported ahead of ContractsList.css so this screen's remaining
// deltas still land last.
import '../../../components/ui/HMSTable.css';
import './ContractsList.css';
import '../LegalPills.css';

// Deterministic IMT-green palette for the grouping accent bar. Port of
// the generator in TradeOperationsLayout.js — same hashing function so
// the same group key always renders the same colour. Solid colours
// only (no gradients) to match the recap table.
const GROUP_COLORS = [
  ['#4a6b25', '#FFFFFF'],
  ['#5a7b35', '#FFFFFF'],
  ['#3a5b15', '#FFFFFF'],
  ['#65a30d', '#FFFFFF'],
  ['#4d7c0f', '#FFFFFF'],
  ['#3f6212', '#FFFFFF'],
  ['#15803d', '#FFFFFF'],
  ['#166534', '#FFFFFF'],
  ['#2f855a', '#FFFFFF'],
  ['#38a169', '#FFFFFF'],
  ['#276749', '#FFFFFF'],
  ['#6b8e23', '#FFFFFF'],
  ['#557a2e', '#FFFFFF'],
  ['#4b6b1f', '#FFFFFF'],
  ['#3d5a1f', '#FFFFFF'],
];

const generateGroupColor = (groupId) => {
  const hash = String(groupId).split('').reduce(
    (acc, char) => char.charCodeAt(0) + ((acc << 5) - acc),
    0,
  );
  const [backgroundColor, color] = GROUP_COLORS[Math.abs(hash) % GROUP_COLORS.length];
  return { backgroundColor, color };
};

// Canonical group key — both legs of a pair compute the same key
// because we always take the lower of the two contract IDs.
const buildGroupKey = (contract) => {
  if (contract.paired_contract) {
    const lower = Math.min(contract.id, contract.paired_contract);
    const upper = Math.max(contract.id, contract.paired_contract);
    return `pair_${lower}_${upper}`;
  }
  return `single_${contract.id}`;
};

// Within a paired group we always want Sale above Purchase so the
// table reads top-to-bottom in the same order traders see in the
// Contract Creator modal.
const PAIR_ORDER = { Sa: 0, Pu: 1, Tr: 2, Other: 3 };
const sortWithinGroup = (a, b) => (
  (PAIR_ORDER[a.contract_type] ?? 99) - (PAIR_ORDER[b.contract_type] ?? 99)
);

// The accent bar is intentionally text-free — it carries a single
// link icon to signal "these rows are linked". Any text we'd put here
// (recap number, enquiry id) is either already in a column or
// illegible at 7px rotated, so we leave info display to the columns.

// The expiry flag's tone on the shared status chip.
const FLAG_STATUS = { warning: 'pending', danger: 'overdue', neutral: undefined };

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch (_e) {
    return value;
  }
};

const formatPeriod = (start, end) => {
  if (!start && !end) return '—';
  return `${formatDate(start)} – ${formatDate(end)}`;
};

const ContractsList = ({ listParams = {}, onRowClick, refreshKey = 0 }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Stable JSON signature so the effect doesn't re-fire when the parent
  // re-renders without changing the filter params.
  const listParamsSignature = useMemo(
    () => JSON.stringify(listParams || {}),
    [listParams],
  );

  // Debounce the free-text search by 300ms so we don't fire a request on
  // every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    const fetchContracts = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = { ...JSON.parse(listParamsSignature) };
        if (debouncedQuery) params.q = debouncedQuery;
        const data = await legalContractService.list(params);
        if (cancelled) return;
        // The viewset returns either a paginated envelope or a flat array
        // depending on whether DRF pagination is configured. Handle both.
        if (Array.isArray(data)) {
          setContracts(data);
        } else if (data && Array.isArray(data.results)) {
          setContracts(data.results);
        } else {
          setContracts([]);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.detail
            || err?.message
            || 'Failed to load contracts.',
        );
        setContracts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchContracts();
    return () => {
      cancelled = true;
    };
    // ``refreshKey`` is a parent-controlled counter — incrementing it
    // forces a re-fetch without changing the actual filter params (used
    // by the TimeNavigationBar refresh button on LegalDashboard).
  }, [listParamsSignature, debouncedQuery, refreshKey]);

  // Re-bucket contracts into pairs + singles, preserving the
  // server-side sort order (newest pair first). Within each pair Sale
  // sits above Purchase. The flat ``displayRows`` array carries the
  // positional flags every row needs to apply the right grouped /
  // first / last classes without re-computing on every render.
  const displayRows = useMemo(() => {
    if (!contracts.length) return [];
    const buckets = new Map();
    const order = [];
    contracts.forEach((contract) => {
      const key = buildGroupKey(contract);
      if (!buckets.has(key)) {
        buckets.set(key, []);
        order.push(key);
      }
      buckets.get(key).push(contract);
    });

    const flat = [];
    const total = contracts.length;
    let tableIndex = 0;
    order.forEach((key) => {
      const bucket = buckets.get(key);
      bucket.sort(sortWithinGroup);
      const groupSize = bucket.length;
      const isPaired = groupSize > 1;
      bucket.forEach((contract, i) => {
        flat.push({
          contract,
          groupKey: key,
          groupSize,
          isPaired,
          isFirstInGroup: i === 0,
          isLastInGroup: i === groupSize - 1,
          isFirstInTable: tableIndex === 0,
          isLastInTable: tableIndex === total - 1,
        });
        tableIndex += 1;
      });
    });
    return flat;
  }, [contracts]);

  const showEmptyState = !loading && !error && contracts.length === 0;

  return (
    <div className="legal-contracts-list">
      <div className="legal-contracts-list__toolbar">
        <div className="legal-contracts-list__search">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="legal-contracts-list__search-icon"
          />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by contract number, counterparty, or terms..."
            aria-label="Search contracts"
          />
        </div>
        <div className="legal-contracts-list__count" aria-live="polite">
          {loading ? '' : `${contracts.length} contract${contracts.length === 1 ? '' : 's'}`}
        </div>
      </div>

      {loading && (
        /* Skeleton in the shape of the contracts table that is coming, so the
           panel does not resize under the reader when the rows land. */
        <div className="legal-contracts-list__loading">
          <HMSSkeleton shape="title" />
          <HMSSkeleton lines={6} />
        </div>
      )}

      {error && !loading && (
        <div className="legal-contracts-list__error" role="alert">
          {error}
        </div>
      )}

      {showEmptyState && (
        /* `card` because the local LegalEmptyState this replaces was wrapped
           in a translucent white panel of its own — the table floats on the
           `.legal-screen` gradient, so the block needs a surface to read on. */
        <HMSEmptyState
          variant="card"
          icon={<FontAwesomeIcon icon={debouncedQuery ? faFile : faFileSignature} />}
          title={
            debouncedQuery
              ? 'No contracts match your search'
              : 'No contracts yet'
          }
          hint={
            debouncedQuery
              ? 'Try a different search term, or clear the search to see every contract.'
              : (listParams.commodity_type === 'freight'
                ? 'Freight contracts are filed automatically the first time a Freight Recap is sent from Trade Ops (Review & Send).'
                : listParams.commodity_type === 'solids'
                  ? 'Cargo contracts are created from a saved sale or purchase recap in Trade Ops Cargo (Create Contract, under the contract number).'
                  : listParams.commodity_type === 'general'
                    ? 'Contracts that do not come from a deal are added here with Add contract, by uploading the file.'
                    : 'Contracts are minted automatically when a bunker enquiry is workflowed into recaps. Open Trade Ops Bunkers to advance an enquiry.')
          }
        />
      )}

      {!loading && !error && contracts.length > 0 && (
        <div className="table-container hms-table-wrap">
          <table className="data-table hms-table legal-contracts-table">
            <thead>
              <tr>
                <th className="hms-deals-grouping-accent-header" aria-hidden="true"></th>
                <th>Contract No.</th>
                <th>Type</th>
                <th>Position</th>
                <th>Entity</th>
                <th>Counterparty</th>
                <th>Commence</th>
                <th>Period</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Linked Recap</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((entry) => {
                const {
                  contract,
                  groupSize,
                  isPaired,
                  isFirstInGroup,
                  isLastInGroup,
                  isFirstInTable,
                  isLastInTable,
                } = entry;
                const rowClass = [
                  'clickable-row',
                  isPaired ? 'grouped-row' : '',
                  isPaired && isFirstInGroup ? 'grouped-first' : '',
                  isPaired && isLastInGroup ? 'grouped-last' : '',
                  isFirstInTable ? 'table-first' : '',
                  isLastInTable ? 'table-last' : '',
                ].filter(Boolean).join(' ');
                return (
                  <tr
                    key={contract.id}
                    className={rowClass}
                    onClick={() => onRowClick && onRowClick(contract)}
                  >
                    <td className="hms-deals-grouping-accent-cell">
                      {isPaired && isFirstInGroup && (
                        <div
                          className="hms-deals-grouping-accent-bar"
                          style={{
                            ...generateGroupColor(entry.groupKey),
                            // Height spans every row in the group plus
                            // the inter-row borders, minus the top/bot
                            // 8px breathing space the bar reserves.
                            height: `calc(${groupSize} * 100% + ${(groupSize - 1) * 1}px - 16px)`,
                          }}
                          title="Linked contracts"
                        >
                          <span className="hms-deals-grouping-accent-bar-icon">
                            <FontAwesomeIcon icon={faLink} />
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="legal-contracts-cno">
                      {contract.contract_number}
                    </td>
                    <td>{contract.contract_type_display || contract.contract_type}</td>
                    <td>
                      {contract.contract_position_display || contract.contract_position}
                    </td>
                    <td>{contract.entity_name || '—'}</td>
                    <td>
                      {contract.counterparty_name || '—'}
                      {contract.counterparty_in_crm === false && (
                        <span className="legal-contracts-review">Not in CRM</span>
                      )}
                    </td>
                    <td>{formatDate(contract.commence_date)}</td>
                    <td>{formatPeriod(contract.period_start, contract.period_end)}</td>
                    <td>
                      {contract.status_display || '—'}
                      {contract.legal_review_status && contract.legal_review_status !== 'none' && (
                        <span className="legal-contracts-review">
                          {contract.legal_review_status_display}
                        </span>
                      )}
                    </td>
                    <td className="legal-contracts-expiry">
                      {contract.no_expiry ? null : (expiryText(contract) || '—')}
                      {expiryFlag(contract) && (
                        <HMSBadge status={FLAG_STATUS[expiryFlag(contract).tone]}>
                          {expiryFlag(contract).label}
                        </HMSBadge>
                      )}
                    </td>
                    <td>{contract.trade_recap_number || contract.freight_recap_number || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ContractsList;
